import "../sellscreendialogs.scss";
import * as React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type ThunkDispatch } from "redux-thunk";
import { hideDialog } from "../../../../Actions/Dialogs/DialogActions";
import { addPaymentToSale, type SaleAddPaymentOptions } from "../../../../Actions/SellScreen/SellScreenPaymentActions";
import Button from "../../../../Common/Buttons/Button";
import { HideOnErrorBoundary } from "../../../../Common/Misc/HideOnErrorBoundary";
import PopupManager, { type PopupId } from "../../../../Common/Popups/PopupManager";
import Receipt from "../../../../Common/Receipts/Receipt";
import { type SalePaymentStatus, type SaleState } from "../../../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import { type RootState } from "../../../../Reducers/ShopfrontReducers";
import ReceiptHelper from "../../../../Utilities/Domain/ReceiptHelper";
import AutoLogout from "../../../../Utilities/Domain/Users/AutoLogout";
import {
    ApplicationEventsFromShopfront,
    ApplicationEventsToShopfront,
} from "../../../../Utilities/Platform/Applications/ApplicationEvents";
import { getApplicationManager } from "../../../../Utilities/Platform/Applications/ApplicationUtilities";
import {
    UnsupportedApplicationEventError,
} from "../../../../Utilities/Platform/Applications/UnsupportedApplicationEventError";
import FontAwesomeLight from "../../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { getLogLevel, LogLevel } from "../../../../Utilities/Platform/Logging";
import { deleteOption, setOption } from "../../../../Utilities/Platform/Options/Options";
import {
    GATEWAY_PAYMENT_EVENTS,
    SHOPFRONT_PAYMENT_DATA_TYPES,
    SHOPFRONT_PAYMENT_EVENTS,
} from "../../../../Utilities/Platform/PaymentGateway/PaymentGatewayEvents";
import { getVendorSubdomain } from "../../../../Utilities/Platform/Request";
import { print } from "../../../../Utilities/UI/PrintHelper";
import { printTextReceipt } from "../../../../Workers/HardwareConnect/HardwareConnectEvents";

function mapStateToProps(state: RootState): {
    sale: SaleState;
    user: {
        id: string;
        name: string;
    };
} {
    return {
        sale: state.sellscreen.sale,
        user: {
            id  : state.user.id || "unknown",
            name: state.user.id === false ? "unknown" : encodeURIComponent(state.user.name),
        },
    };
}

const connector = connect(mapStateToProps);

interface Props extends ConnectedProps<typeof connector> {
    dispatch: ThunkDispatch<any, any, any>;
    amount: number;
    method: string;
    options: SaleAddPaymentOptions;
    gateway: string;
    onComplete?: (method: string, amount: number, options: SaleAddPaymentOptions) => void;
    paymentId: string;
    isRetry: boolean;
}

interface State {
    loading: boolean;
    timedOut: false | {
        cancelled: boolean;
        force: boolean;
    };
    receipt: string;
    noReturn: boolean;
    cancelEnabled: boolean;
    dataRequested: boolean;
}

interface FinishedPayment {
    status: "approved" | "declined" | "cancelled";
    amount: number;
    cashout?: number;
    metaData?: Record<string, unknown>;
    subtype?: string;
}

const PING_TIMEOUT = 10000;
const PING_INTERVAL = 10000;

function mapStatus(status: FinishedPayment["status"]): SalePaymentStatus {
    switch(status) {
        case "approved":
            return "completed";
        case "declined":
            return "failed";
        default:
            return status;
    }
}

export class Integration extends React.Component<Props, State> {
    protected pingRequests: Record<number, ReturnType<typeof setTimeout>>;

    protected pingInterval: null | ReturnType<typeof setInterval>;
    protected finishAfterPrint: false | FinishedPayment;
    protected forceCancelTimeout: null | ReturnType<typeof setTimeout>;
    protected cancelEnableTimeout: false | ReturnType<typeof setTimeout>;
    protected autoLogoutInterval: null | ReturnType<typeof setInterval>;
    protected frameRef: React.RefObject<HTMLIFrameElement | null>;
    protected multiplier: number;
    protected receipt: null | string;
    protected errorId: PopupId | null = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            loading      : true,
            timedOut     : false,
            receipt      : "",
            noReturn     : false,
            cancelEnabled: false,
            dataRequested: false,
        };

        // Prepare for ping requests
        this.pingRequests = {};
        this.pingInterval = null;

        this.finishAfterPrint = false;

        this.forceCancelTimeout = null;
        this.cancelEnableTimeout = false;

        this.autoLogoutInterval = null;

        this.frameRef = React.createRef();

        this.multiplier = 1;
        this.receipt = null;

        this.displayFrame = this.displayFrame.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.sendMessage = this.sendMessage.bind(this);
        this.receiveMessage = this.receiveMessage.bind(this);
        this.finishPayment = this.finishPayment.bind(this);
        this.handlePing = this.handlePing.bind(this);
        this.handlePong = this.handlePong.bind(this);
        this.handleTimeout = this.handleTimeout.bind(this);
        this.startTimers = this.startTimers.bind(this);
        this.clearTimers = this.clearTimers.bind(this);
        this.handleNoReturn = this.handleNoReturn.bind(this);
        this.handleReconnect = this.handleReconnect.bind(this);
        this.handleTimeoutCancel = this.handleTimeoutCancel.bind(this);
        this.handleForceCancel = this.handleForceCancel.bind(this);
    }

    public componentDidMount(): void {
        // Add to the transaction in progress
        setOption("__integration_payment_in_progress", {
            gateway: this.props.gateway,
            method : this.props.method,
            amount : this.props.amount,
            options: this.props.options,
            id     : this.props.paymentId,
        });

        window.addEventListener("message", this.receiveMessage);
        this.startTimers();

        this.cancelEnableTimeout = setTimeout(() => {
            this.setState({
                cancelEnabled: true,
            });
        }, 5000);

        this.autoLogoutInterval = setInterval(this.delayAutoLogout, 1000);
    }

    public componentWillUnmount(): void {
        window.removeEventListener("message", this.receiveMessage);

        if(this.forceCancelTimeout) {
            clearTimeout(this.forceCancelTimeout);
        }

        if(this.cancelEnableTimeout !== false) {
            clearTimeout(this.cancelEnableTimeout);
        }

        if(this.autoLogoutInterval) {
            clearInterval(this.autoLogoutInterval);
        }
    }

    protected catchReceiptError = (): void => {
        if(this.errorId) {
            if(PopupManager.isVisible(this.errorId)) {
                return;
            }
        }

        this.errorId = PopupManager.error("We've encountered an error and were unable to print the receipt");
    };

    protected delayAutoLogout(): void {
        AutoLogout.updateTimeEvent(Date.now());
    }

    protected displayFrame(): void {
        this.setState({
            loading: false,
        });
    }

    protected sendMessage(type: SHOPFRONT_PAYMENT_EVENTS, data: any = {}): void {
        if(this.frameRef.current && this.frameRef.current.contentWindow) {
            if(getLogLevel() >= LogLevel.NORMAL) {
                console.log(`Sending ${type} payment event to payment gateway`);
            }

            this.frameRef.current.contentWindow.postMessage(JSON.stringify({
                type: type,
                data: data,
            }), "*");
        }
    }

    protected receiveMessage(e: MessageEvent): void {
        let data = e.data;

        if(typeof data === "string") {
            data = JSON.parse(e.data);
        }

        if(!data) {
            console.error("Unknown message sent to payment gateway", e);

            return;
        }

        if(
            (this.props.gateway.charAt(0) !== "/" && !this.props.gateway.includes(e.origin)) ||
            e.source !== this.frameRef.current?.contentWindow
        ) {
            return;
        }

        if(data.type === ApplicationEventsToShopfront.LISTENERS.INTERNAL_PAGE_MESSAGE) {
            getApplicationManager().getApplication(data.clientId)
                ?.send(ApplicationEventsFromShopfront.INTERNAL_PAGE_MESSAGE, {
                    method : "PAYMENT_API",
                    url    : data.from,
                    message: data.message,
                })
                .catch(e => {
                    if(e instanceof UnsupportedApplicationEventError) {
                        return;
                    }

                    throw e;
                });

            return;
        }

        if(data.type) {
            this.handleMessage(data.type, data.data);
        } else {
            // console.error("Unknown message sent to payment gateway", e);
        }
    }

    protected handleMessage(
        type: GATEWAY_PAYMENT_EVENTS,
        data: any
    ): void {
        switch(type) {
            case GATEWAY_PAYMENT_EVENTS.REQUEST_DATA:
                console.log("Received REQUEST_DATA payment event");

                // eslint-disable-next-line no-case-declarations
                let amount = this.props.amount;
                // eslint-disable-next-line no-case-declarations
                const refund = this.props.options.refund || amount < 0;

                if(refund && amount < 0) {
                    this.multiplier = -1;
                    amount *= this.multiplier;
                }

                this.sendMessage(SHOPFRONT_PAYMENT_EVENTS.DATA, {
                    type     : SHOPFRONT_PAYMENT_DATA_TYPES.SALE,
                    sale     : this.props.sale,
                    cashout  : this.props.options.cashout,
                    amount   : amount,
                    reference: this.props.paymentId.replace(/-/g, ""),
                    refund   : (refund && this.props.options.related) || refund,
                    isRetry  : this.props.isRetry,
                    user     : this.props.user,
                    vendor   : getVendorSubdomain(),
                });

                this.setState({
                    dataRequested: true,
                });

                break;
            case GATEWAY_PAYMENT_EVENTS.PONG:
                if(getLogLevel() === LogLevel.VERBOSE) {
                    console.log("Received PONG payment event");
                }

                if(typeof data === "number") {
                    this.handlePong(data);
                } else {
                    console.error("Failed to receive valid ping ID from payment gateway.");
                }

                break;
            case GATEWAY_PAYMENT_EVENTS.ERROR:
                console.log("Received ERROR payment event");
                // TODO: ERROR HANDLING
                console.error(data);
                break;
            case GATEWAY_PAYMENT_EVENTS.RECEIPT:
                console.log("Received RECEIPT payment event");

                if(data.print) {
                    this.errorId = null;
                    const components = [
                        { type: "eftpos", value: null, style: {} },
                    ];

                    let requestCallback: number | null = null;
                    // eslint-disable-next-line @typescript-eslint/no-this-alias
                    const self = this;
                    const callback = (): void => {
                        const afterCallback = function(): void {
                            self.setState({
                                receipt: "",
                            }, function() {
                                if(self.finishAfterPrint) {
                                    self.finishPayment(self.finishAfterPrint);
                                }
                            });
                        };

                        if(requestCallback !== null) {
                            cancelAnimationFrame(requestCallback);
                        }

                        requestCallback = requestAnimationFrame(afterCallback);
                    };

                    data.receiptText = "" + data.receiptText;

                    let width = 0;
                    const split = data.receiptText.split("\n");

                    for(let i = 0, l = split.length; i < l; i++) {
                        width = Math.max(split[i].length, width);
                    }

                    printTextReceipt(ReceiptHelper.createTextReceipt(components, {
                        eftpos: data.receiptText,
                    }, width), (err) => {
                        let ignoreError = false;

                        if(err && process.env.NODE_ENV !== "production") {
                            ignoreError = true;
                            PopupManager.warning(
                                "Development: Hardware connect isn't detected and receipt requires" +
                                " printing, have skipped to prevent freezing"
                            );
                        }

                        if(err && !ignoreError) {
                            // NOTE: This does not work on the development version of react
                            // Bug: https://github.com/facebook/react/issues/16734

                            // No need to update the state before this point as it's only
                            // used to print the Window
                            this.setState({
                                receipt: "" + data.receiptText,
                            }, () => {
                                // We get the animation frame because this callback will
                                // fire before the rendering occurs in the next tick
                                requestAnimationFrame(() => {
                                    if(this.errorId === null) {
                                        print(false, callback);
                                    }
                                });
                            });
                        } else {
                            callback();
                        }
                    });
                } else {
                    if(!this.receipt) {
                        this.receipt = "";
                    }

                    this.receipt += data.receiptText;
                }

                break;
            case GATEWAY_PAYMENT_EVENTS.FINISH:
                console.log("Received FINISH payment event");

                if(
                    [ "approved", "declined", "cancelled" ].includes(data.status) &&
                    typeof data.amount === "number" &&
                    (
                        typeof data.cashout === "undefined" ||
                        (typeof data.cashout === "boolean" && data.cashout === false) ||
                        typeof data.cashout === "number" ||
                        data.cashout === null
                    ) &&
                    (typeof data.metaData === "undefined" || typeof data.metaData === "object") &&
                    (typeof data.subtype === "undefined" || typeof data.subtype === "string")
                ) {
                    this.finishPayment(data);
                } else {
                    PopupManager.error(
                        "The payment gateway provided an invalid result, " +
                        "please check and confirm the amount was successfully paid"
                    );

                    console.error(
                        "Received an invalid FINISH payment result:\n" +
                        `You provided ${JSON.stringify(data, null, 4)}\n\n` +
                        "However the expected result is:\n" +
                        "{\n" +
                        "    status: \"approved\" | \"declined\" | \"cancelled\",\n" +
                        "    amount: number,\n" +
                        "    cashout?: number | undefined,\n" +
                        "    metaData?: object | undefined,\n" +
                        "    subtype?: string | undefined,\n" +
                        "}\n" +
                        "Please check your integration and try again"
                    );

                    this.handleForceCancel();
                }

                break;
            case GATEWAY_PAYMENT_EVENTS.NO_RETURN:
                if(getLogLevel() >= LogLevel.NORMAL) {
                    console.log("Received point of NO_RETURN payment event");
                }

                if(typeof data === "boolean") {
                    this.handleNoReturn(data);
                } else {
                    console.error("Received invalid NO_RETURN payment event: data must be a boolean");
                }

                break;
            case GATEWAY_PAYMENT_EVENTS.CONNECTED:
                if(getLogLevel() === LogLevel.VERBOSE) {
                    console.log("Received CONNECTED payment event");
                }

                this.setState({
                    cancelEnabled: true,
                });
                break;
            case GATEWAY_PAYMENT_EVENTS.ACKNOWLEDGE_CLOSE:
                // No need to do anything here, we already have a 200ms timeout running at this point
                break;
            default:
                console.error("Unknown message sent from Payment Gateway", type, data);
                break;
        }
    }

    protected handlePong(data: number): void {
        clearTimeout(this.pingRequests[data]);

        delete this.pingRequests[data];
    }

    protected handlePing(): void {
        const pingId = Date.now();

        this.pingRequests[pingId] = setTimeout(this.handleTimeout, PING_TIMEOUT);

        this.sendMessage(SHOPFRONT_PAYMENT_EVENTS.PING, pingId);
    }

    protected handleTimeout(): void {
        if(getLogLevel() >= LogLevel.NORMAL) {
            console.log("PING TIMEOUT");
        }

        this.setState({
            timedOut: {
                cancelled: false,
                force    : false,
            },
        });

        this.clearTimers();
    }

    protected startTimers(): void {
        this.pingInterval = setInterval(this.handlePing, PING_INTERVAL);
    }

    protected clearTimers(): void {
        if(this.pingInterval) {
            clearInterval(this.pingInterval);

            const requests = Object.values(this.pingRequests);

            for(let i = 0; i < requests.length; i++) {
                const request = requests[i];

                clearTimeout(request);
            }

            this.pingRequests = {};
        }
    }

    protected handleCancel(): void {
        if(!this.state.dataRequested) {
            console.log("Cancel pressed before sending data, forcing the cancellation");
            this.handleForceCancel();

            return;
        }

        console.log("Sending cancel to integration");
        this.sendMessage(SHOPFRONT_PAYMENT_EVENTS.CANCEL);
    }

    protected finishPayment(payment: FinishedPayment): void {
        // Remove from the transaction in progress
        deleteOption("__integration_payment_in_progress");

        if(this.state.receipt !== "") {
            console.log("Received payment, but waiting on print to finish");
            this.finishAfterPrint = payment;

            return;
        }

        const status = mapStatus(payment.status);
        const method = this.props.method;
        const amount = (Math.round(payment.amount * 100) / 100) * this.multiplier;
        const options: SaleAddPaymentOptions = {
            ...this.props.options,
            status,
            cashout : typeof payment.cashout === "undefined" ? undefined : Math.round(payment.cashout * 100) / 100,
            receipt : this.receipt === null ? undefined : this.receipt,
            exact   : status === "completed" ? false : this.props.options.exact,
            metaData: payment.metaData,
            subtype : payment.subtype,
        };

        if(payment.metaData?.tyroProPresumed) {
            PopupManager.info(
                "Tyro didn't provide a full response to determine if the transaction is complete, but we've presumed " +
                "it was approved, you may want to check the terminal to ensure the transaction was approved.",
                false,
                false,
                15000
            );
        }

        console.log(`Finished payment, status was ${options.status}`, options, payment);

        this.clearTimers();
        this.sendMessage(SHOPFRONT_PAYMENT_EVENTS.CLOSING);

        setTimeout(() => {
            this.props.dispatch(hideDialog());

            if(this.props.onComplete) {
                this.props.onComplete(method, amount, options);
            } else {
                this.props.dispatch(addPaymentToSale(method, amount, options));
            }
        }, 200);
    }

    protected handleReconnect(): void {
        this.setState({
            timedOut: false,
        });

        this.startTimers();
    }

    protected handleTimeoutCancel(): void {
        this.setState({
            timedOut: {
                cancelled: true,
                force    : false,
            },
        });

        console.log("The user has forced a cancellation due to the integration timing out");
        this.handleCancel();

        this.forceCancelTimeout = setTimeout(() => {
            this.setState({
                timedOut: {
                    cancelled: true,
                    force    : true,
                },
            });
        }, 5000);
    }

    protected handleForceCancel(): void {
        this.finishPayment({
            status: "cancelled",
            amount: this.props.amount,
        });
    }

    protected handleNoReturn(pastPoint: boolean): void {
        this.setState({
            noReturn: pastPoint,
        });
    }

    public render(): React.ReactNode {
        const classes = [ "integration" ];
        const wrapperClasses = [ "payment-integration" ];

        let loading = null;
        let cancel = null;

        if(this.state.loading) {
            classes.push("hidden");
            wrapperClasses.push("loading");

            loading = (
                <span className="loading-spinner">
                    <FontAwesomeLight icon="spinner-third" spin />
                </span>
            );
        } else if(this.state.timedOut) {
            classes.push("hidden");

            if(this.state.timedOut.cancelled) {
                loading = (
                    <div className="integration timeout">
                        <div className="timeout-heading">
                            Cannot connect to payment gateway
                        </div>
                        <div className="timeout-content">
                            Cancel request has been sent
                        </div>
                        <div className="timeout-buttons">
                            <div className="full">
                                <Button
                                    type="button"
                                    classes={[ "button-save" ]}
                                    onClick={this.handleForceCancel}
                                    disabled={!this.state.timedOut.force}
                                >
                                    Force Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            } else {
                loading = (
                    <div className="integration timeout">
                        <div className="timeout-heading">
                            Cannot connect to payment gateway
                        </div>
                        <div className="timeout-content">
                            What do you want to do?
                        </div>
                        <div className="timeout-buttons">
                            <div className="half">
                                <Button
                                    type="button"
                                    classes={[ "button-retry" ]}
                                    onClick={this.handleReconnect}
                                >
                                    Reconnect
                                </Button>
                            </div>
                            <div className="half">
                                <Button
                                    type="button"
                                    classes={[ "button-save" ]}
                                    onClick={this.handleTimeoutCancel}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            }
        } else if(!this.state.noReturn && this.state.cancelEnabled) {
            cancel = (
                <Button type="button" classes={[ "button-cancel" ]} onClick={this.handleCancel}>
                    Cancel
                </Button>
            );
        }

        let receipt = null;

        if(this.state.receipt) {
            receipt = (
                <Receipt
                    type="receipt-text"
                    padding={null}
                    data={{
                        eftpos  : this.state.receipt,
                        invoices: [],
                        payments: [],
                        total   : {
                            total       : 0,
                            invoiceCount: 0,
                            outstanding : 0,
                        },
                        text: {
                            completedAt     : "",
                            currentTimestamp: "",
                            user            : null,
                            outlet          : null,
                            register        : null,
                            customer        : null,
                            status          : "",
                            reference       : "",
                            payment_method  : {
                                name: "",
                            },
                        },
                    }}
                    format={[
                        { type: "eftpos", value: null, style: {} },
                    ]}
                />
            );
        }

        return (
            <div className={wrapperClasses.join(" ")}>
                <p className="payment-integration-dont-close">Do not leave the current tab.</p>
                {loading}
                <iframe
                    className={classes.join(" ")}
                    src={this.props.gateway}
                    onLoad={this.displayFrame}
                    ref={this.frameRef}
                    allow="local-network-access"
                />
                {cancel}
                <HideOnErrorBoundary onCaught={this.catchReceiptError}>
                    {receipt}
                </HideOnErrorBoundary>
            </div>
        );
    }
}

export default connector(Integration);
